import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
    useGetLoanApplicationByTask,
    useUploadDocument,
    useUploadSignedLoanAgreement,
    usePrepareLoanAgreementData,
    useGenerateLoanAgreementPDF,
    useGenerateLoanPreAgreementPDF,
    usePrepareLoanPreAgreementData,
    useGetDocumentsByLoanApplication,
    cancelLoanApplication
} from '../../../services/loanService';
import { UploadDocumentBodyDocumentType } from '../../../services/schemas/loanServiceSchemas/uploadDocumentBodyDocumentType';
import { LoanApplication } from '../../../services/schemas/loanServiceSchemas';
import { LoanPreAgreementData } from '../../../services/schemas/loanServiceSchemas/loanPreAgreementData';
import { LoanAgreementData } from '../../../services/schemas/loanServiceSchemas/loanAgreementData';
import PreAgreementForm from './PreAgreementForm';
import LoanAgreementForm from './LoanAgreementForm';
import './LoanAgreementDetails.css';

interface AgreementDetails {
    signedDocument: File | null;
    notes: string;
}

interface SignedPreAgreement {
    signedPreAgreementDocument: File | null;
    preAgreementNotes: string;
}

type WorkflowStep = 'prepare' | 'upload-pre-agreement' | 'prepare-agreement' | 'upload-final-agreement' | 'completed';

const LoanAgreementDetails = () => {
    const { taskId } = useParams<{ taskId: string }>();
    const navigate = useNavigate();

    // State for loading and errors
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [showLoadingOverlay, setShowLoadingOverlay] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    // The upload is recorded as the client's signature, so the uploader confirms it really is the signed copy
    const [confirmedSigned, setConfirmedSigned] = useState<boolean>(false);
    const user = useSelector((state: any) => state.auth.user);
    const [currentStep, setCurrentStep] = useState<WorkflowStep>('prepare');
    const [generatedPdfBlob, setGeneratedPdfBlob] = useState<Blob | null>(null);
    const [generatedAgreementPdfBlob, setGeneratedAgreementPdfBlob] = useState<Blob | null>(null);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [hasAutoNavigated, setHasAutoNavigated] = useState<boolean>(false);

    // Agreement states
    const [agreement, setAgreement] = useState<AgreementDetails>({
        signedDocument: null,
        notes: ''
    });

    const [preAgreement, setPreAgreement] = useState<SignedPreAgreement>({
        signedPreAgreementDocument: null,
        preAgreementNotes: ''
    });

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [isCancelling, setIsCancelling] = useState(false);

    // Fetch loan application by task ID
    const {
        data: loanApplication,
        isLoading: isLoadingApplication,
        error: applicationError
    } = useGetLoanApplicationByTask(taskId || '', {
        query: {
            enabled: !!taskId
        }
    });

    // Fetch documents for the loan application to check for existing PRE_LOAN_AGREEMENT
    const {
        data: documents,
        isLoading: isLoadingDocuments
    } = useGetDocumentsByLoanApplication(loanApplication?.id || '', undefined, {
        query: {
            enabled: !!loanApplication?.id
        }
    });

    // Check for existing PRE_LOAN_AGREEMENT document and set initial step (only once on initial load)
    useEffect(() => {
        // Only auto-navigate on initial load - check hasAutoNavigated flag
        // This prevents re-triggering when user manually navigates back
        if (hasAutoNavigated) {
            return; // Already processed, don't auto-navigate again
        }
        
        // Only auto-navigate if documents are loaded and we're on the initial prepare step
        if (documents && documents.length > 0 && loanApplication?.id) {
            const preLoanAgreementDoc = documents.find(doc => doc.documentType === 'PRE_LOAN_AGREEMENT');
            if (preLoanAgreementDoc) {
                // If PRE_LOAN_AGREEMENT exists, set step to upload-pre-agreement
                setCurrentStep('upload-pre-agreement');
                setHasAutoNavigated(true);
                // Map the existing document to preAgreement state
                // Create a synthetic File object to represent the existing document
                const file = new File([''], preLoanAgreementDoc.name || 'pre-agreement-document.pdf', {
                    type: preLoanAgreementDoc.contentType || 'application/pdf'
                });
                setPreAgreement(prev => ({
                    ...prev,
                    signedPreAgreementDocument: file
                }));
            } else {
                // No document found, mark as checked so we don't check again
                setHasAutoNavigated(true);
            }
        }
    }, [documents, loanApplication?.id, hasAutoNavigated]);

    // Fetch pre-agreement data
    const {
        data: preAgreementData,
        isLoading: isLoadingPreAgreementData,
        error: preAgreementDataError
    } = usePrepareLoanPreAgreementData(loanApplication?.id || '', {
        query: {
            enabled: !!loanApplication?.id && currentStep === 'prepare'
        }
    });

    // Fetch loan agreement data
    const {
        data: agreementData,
        isLoading: isLoadingAgreementData,
        error: agreementDataError
    } = usePrepareLoanAgreementData(loanApplication?.id || '', {
        query: {
            enabled: !!loanApplication?.id && currentStep === 'prepare-agreement'
        }
    });

    // Upload document mutation
    const uploadDocumentMutation = useUploadDocument();

    // Upload signed agreement mutation
    const uploadSignedAgreementMutation = useUploadSignedLoanAgreement();

    // Generate PDF mutations
    const generatePreAgreementPdfMutation = useGenerateLoanPreAgreementPDF();
    const generateAgreementPdfMutation = useGenerateLoanAgreementPDF();

    // Handle pre-agreement form submission and PDF generation
    const handlePreAgreementSubmit = async (formData: LoanPreAgreementData) => {
        try {
            setIsUploading(true);
            setShowLoadingOverlay(true);
            setUploadError(null);

            const pdfBlob = await generatePreAgreementPdfMutation.mutateAsync({
                data: formData
            });

            setGeneratedPdfBlob(pdfBlob);
            setCurrentStep('upload-pre-agreement');
            toast.success('Pre-agreement PDF generated successfully');

        } catch (error) {
            console.error('Error generating PDF:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to generate PDF';
            setUploadError(errorMessage);
            toast.error('Failed to generate PDF: ' + errorMessage);
        } finally {
            setIsUploading(false);
            setShowLoadingOverlay(false);
        }
    };

    // Handle loan agreement form submission and PDF generation
    const handleAgreementSubmit = async (formData: LoanAgreementData) => {
        try {
            setIsUploading(true);
            setShowLoadingOverlay(true);
            setUploadError(null);

            const pdfBlob = await generateAgreementPdfMutation.mutateAsync({
                data: formData
            });

            setGeneratedAgreementPdfBlob(pdfBlob);
            setCurrentStep('upload-final-agreement');
            toast.success('Loan agreement PDF generated successfully');

        } catch (error) {
            console.error('Error generating loan agreement PDF:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to generate loan agreement PDF';
            setUploadError(errorMessage);
            toast.error('Failed to generate loan agreement PDF: ' + errorMessage);
        } finally {
            setIsUploading(false);
            setShowLoadingOverlay(false);
        }
    };

    // Handle pre-agreement file upload
    const handlePreAgreementFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        try {
            setIsUploading(true);
            setShowLoadingOverlay(true);
            setUploadError(null);
            const file = e.target.files[0];

            if (file.size > 100 * 1024 * 1024) {
                toast.error(`File size exceeds 100MB limit: ${file.name}`);
                return;
            }

            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                toast.error(`Invalid file type: ${file.name}. Please upload PDF, JPG, or PNG files.`);
                return;
            }

            // Generate a unique filename by appending milliseconds
            const timestamp = new Date().getTime().toString().slice(-4);
            const fileExtension = file.name.split('.').pop();
            const baseName = file.name.substring(0, file.name.lastIndexOf('.'));
            const uniqueFileName = `signed_pre_agreement_${baseName}_${timestamp}.${fileExtension}`;

            // Create a new File object with the unique name
            const uniqueFile = new File([file], uniqueFileName, { type: file.type });

            // Upload the document with the unique filename
            await uploadDocumentMutation.mutateAsync({
                loanApplicationId: loanApplication?.id || '',
                data: {
                    file: uniqueFile,
                    documentType: 'PRE_LOAN_AGREEMENT' as UploadDocumentBodyDocumentType,
                    description: 'Uploaded signed pre-agreement document'
                }
            });

            // Update the pre-agreement state with the uploaded file
            setPreAgreement(prev => ({
                ...prev,
                signedPreAgreementDocument: uniqueFile
            }));

            toast.success('Signed pre-agreement uploaded successfully');

        } catch (error) {
            console.error('Error uploading pre-agreement file:', error);
            const errorMessage = error instanceof Error ? error.message : 'An error occurred while uploading the file';
            setUploadError(errorMessage);
            toast.error('Failed to upload file: ' + errorMessage);
        } finally {
            setIsUploading(false);
            setShowLoadingOverlay(false);
            if (e.target) {
                e.target.value = '';
            }
        }
    };

    // Handle final agreement file upload
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        try {
            setIsUploading(true);
            setShowLoadingOverlay(true);
            setUploadError(null);
            const file = e.target.files[0];

            if (file.size > 100 * 1024 * 1024) {
                toast.error(`File size exceeds 100MB limit: ${file.name}`);
                return;
            }

            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                toast.error(`Invalid file type: ${file.name}. Please upload PDF, JPG, or PNG files.`);
                return;
            }

            // Held here until submit: submitting stores it and records it as signed, in one step,
            // so a file is never on the application without its signature record (or the reverse).
            setAgreement(prev => ({
                ...prev,
                signedDocument: file
            }));
            setConfirmedSigned(false);

        } catch (error) {
            console.error('Error uploading file:', error);
            const errorMessage = error instanceof Error ? error.message : 'An error occurred while uploading the file';
            setUploadError(errorMessage);
            toast.error('Failed to upload file: ' + errorMessage);
        } finally {
            setIsUploading(false);
            setShowLoadingOverlay(false);
            if (e.target) {
                e.target.value = '';
            }
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name.startsWith('preAgreement')) {
            setPreAgreement(prev => ({
                ...prev,
                [name]: value
            }));
        } else {
        setAgreement(prev => ({
            ...prev,
            [name]: value
        }));
        }
    };

    // Download generated PDF
    const handleDownloadPdf = async () => {
        if (!generatedPdfBlob) return;

        try {
            setIsDownloading(true);

            // Add a small delay to show the loading state
            await new Promise(resolve => setTimeout(resolve, 500));

            const url = window.URL.createObjectURL(generatedPdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `TIRHELO-PRE-AGREEMENT-${loanApplication?.applicationNumber || 'document'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Pre-agreement PDF downloaded successfully');
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Failed to download PDF');
        } finally {
            setIsDownloading(false);
        }
    };

    // Move to next step
    const handleProceedToAgreementPreparation = () => {
        if (!preAgreement.signedPreAgreementDocument) {
            toast.error('Please upload the signed pre-agreement first');
            return;
        }
        setCurrentStep('prepare-agreement');
    };

    // Skip pre-agreement generation and go directly to upload
    const handleSkipPreAgreementGeneration = () => {
        setCurrentStep('upload-pre-agreement');
        toast.info('Skipped pre-agreement generation. You can upload your existing signed pre-agreement.');
    };

    // Skip loan agreement generation and go directly to upload
    const handleSkipAgreementGeneration = () => {
        setCurrentStep('upload-final-agreement');
        toast.info('Skipped loan agreement generation. You can upload your existing signed loan agreement.');
    };

    // Go back to previous step
    const handleGoBack = () => {
        switch (currentStep) {
            case 'upload-pre-agreement':
                setCurrentStep('prepare');
                setGeneratedPdfBlob(null);
                // Don't clear the existing document - user can choose to keep it or regenerate
                break;
            case 'prepare-agreement':
                setCurrentStep('upload-pre-agreement');
                break;
            case 'upload-final-agreement':
                setCurrentStep('prepare-agreement');
                setGeneratedAgreementPdfBlob(null);
                break;
            default:
                break;
        }
    };

    const handleSubmit = async () => {
        if (!agreement.signedDocument) {
            toast.error('Please upload a signed agreement document first');
            return;
        }
        if (!confirmedSigned) {
            toast.error('Confirm that this is the agreement signed by the client');
            return;
        }

        try {
            setIsUploading(true);
            setShowLoadingOverlay(true);

            await uploadSignedAgreementMutation.mutateAsync({
                applicationId: loanApplication?.id || '',
                data: {
                    file: agreement.signedDocument,
                    notes: agreement.notes,
                    confirmedSigned: true,
                    uploadedBy: user?.name || user?.username
                }
            });

            toast.success('Signed agreement stored and recorded as signed');
            setIsSubmitted(true);

        } catch (error) {
            console.error('Error submitting agreement:', error);
            const errorMessage = error instanceof Error ? error.message : 'An error occurred while submitting the agreement';
            setUploadError(errorMessage);
            toast.error('Failed to submit agreement: ' + errorMessage);
        } finally {
            setIsUploading(false);
            setShowLoadingOverlay(false);
        }
    };

    const handleCancelApplication = async () => {
        if (!cancelReason.trim()) {
            toast.error('Please provide a reason for cancellation');
            return;
        }
        const appId = loanApplication?.id;
        if (!appId) {
            toast.error('No application ID available');
            return;
        }
        setIsCancelling(true);
        try {
            await cancelLoanApplication(appId, { reason: cancelReason.trim() });
            toast.success('Application cancelled successfully');
            setShowCancelModal(false);
            setCancelReason('');
            navigate('/applications');
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to cancel application';
            toast.error(message);
        } finally {
            setIsCancelling(false);
        }
    };

    // Loading state
    if (isLoadingApplication ||
        isLoadingDocuments ||
        (currentStep === 'prepare' && isLoadingPreAgreementData) ||
        (currentStep === 'prepare-agreement' && isLoadingAgreementData)) {
        return (
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12 text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">
                                {isLoadingApplication
                                    ? 'Loading application details...'
                                    : currentStep === 'prepare'
                                    ? 'Loading pre-agreement data...'
                                    : 'Loading loan agreement data...'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error handling
    if (applicationError ||
        (currentStep === 'prepare' && preAgreementDataError) ||
        (currentStep === 'prepare-agreement' && agreementDataError)) {
        const error = applicationError || preAgreementDataError || agreementDataError;
        return (
            <div className="container-fluid py-4">
                <div className="card">
                    <div className="card-body">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                {applicationError
                                    ? `Failed to load application: ${(applicationError as Error).message}`
                                    : currentStep === 'prepare'
                                    ? `Failed to load pre-agreement data: ${(preAgreementDataError as unknown as Error).message}`
                                    : `Failed to load loan agreement data: ${(agreementDataError as unknown as Error).message}`}
                            </div>
                        </div>
                        <div className="text-center mt-4">
                            <Link to="/work-items" className="btn btn-outline-primary">
                                <i className="bi bi-arrow-left me-2"></i>Back to Work Items
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Display submission success state
    if (isSubmitted) {
        return (
            <div className="container-fluid p-4">
                <div className="card">
                    <div className="card-body text-center py-5">
                        <div className="mb-4">
                            <i className="fa fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h2 className="mb-3">Loan Agreement Submitted Successfully</h2>
                        <p className="text-muted mb-4">The signed loan agreement has been submitted successfully.</p>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => navigate('/work-items')}
                        >
                            <i className="fa fa-arrow-left me-2"></i>
                            Return to Work Items
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Render workflow step content
    const renderStepContent = () => {
        switch (currentStep) {
            case 'prepare':
                if (!preAgreementData) return null;
                return (
                    <div className="card">
                        <div className="card-body">
                            <div className="row justify-content-center">
                                <div className="col-12 col-lg-10 col-xl-8">
                                    <PreAgreementForm
                                        initialData={preAgreementData}
                                        onSubmit={handlePreAgreementSubmit}
                                        onCancel={() => navigate('/work-items')}
                                        onSkip={handleSkipPreAgreementGeneration}
                                        isGenerating={isUploading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'upload-pre-agreement':
                return renderUploadPreAgreementStep();

            case 'prepare-agreement':
                if (!agreementData) return null;
                return (
                    <div className="card">
                        <div className="card-body">
                            <div className="row justify-content-center">
                                <div className="col-12 col-lg-10 col-xl-8">
                                    <LoanAgreementForm
                                        initialData={agreementData}
                                        isIndividual={loanApplication?.loanType === 'PERSONAL'}
                                        onSubmit={handleAgreementSubmit}
                                        onCancel={handleGoBack}
                                        onSkip={handleSkipAgreementGeneration}
                                        isGenerating={isUploading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'upload-final-agreement':
                return renderUploadFinalAgreementStep();

            default:
                return null;
        }
    };

    const renderUploadPreAgreementStep = () => (
        <div className="card">
            <div className="card-header">
                <h6 className="card-title">
                    <i className="fa fa-file-upload text-primary me-2"></i>
                    Upload Signed Pre-Agreement
                </h6>
            </div>
            <div className="card-body">
                {/* Generated PDF Download Section */}
                {generatedPdfBlob && (
                    <div className="alert alert-success mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <i className="fa fa-check-circle me-2"></i>
                                <strong>Pre-agreement PDF generated successfully!</strong>
                                <p className="mb-0 mt-2">Download the generated pre-agreement, get it signed, and upload it back.</p>
                            </div>
                            <button
                                className="btn btn-outline-success"
                                onClick={handleDownloadPdf}
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-download me-2"></i>
                                        Download PDF
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                <p className="text-muted mb-4">
                    Upload the signed pre-agreement document. This document should be signed by all parties before proceeding to the final loan agreement.
                </p>

                {!generatedPdfBlob && !preAgreement.signedPreAgreementDocument && (
                    <div className="alert alert-info mb-4">
                        <div className="d-flex">
                            <div className="me-3">
                                <i className="fa fa-info-circle fa-lg"></i>
                            </div>
                            <div>
                                <p className="mb-0">
                                    <strong>Note:</strong> You skipped the pre-agreement generation step. Please upload your existing signed pre-agreement document.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {preAgreement.signedPreAgreementDocument && !generatedPdfBlob && (
                    <div className="alert alert-success mb-4">
                        <div className="d-flex">
                            <div className="me-3">
                                <i className="fa fa-check-circle fa-lg"></i>
                            </div>
                            <div>
                                <p className="mb-0">
                                    <strong>Pre-agreement document already uploaded:</strong> {preAgreement.signedPreAgreementDocument.name}
                                </p>
                                <small className="text-muted">You can replace it if needed or proceed to prepare the loan agreement.</small>
                            </div>
                        </div>
                    </div>
                )}

                {uploadError && (
                    <div className="alert alert-danger mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <span>{uploadError}</span>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setUploadError(null)}
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                )}

                <div className="document-section mb-4">
                    <div className="row g-4">
                        <div className="col-md-12">
                            <div className="document-upload-card">
                                <div className="document-info">
                                    <h6 className="document-title">
                                        Signed Pre-Agreement <span className="text-danger">*</span>
                                    </h6>
                                    <p className="document-description">Upload the signed pre-agreement document</p>
                                    {preAgreement.signedPreAgreementDocument && (
                                        <div className="document-meta">
                                            <i className="fa fa-check-circle text-success me-2"></i>
                                            <span>{preAgreement.signedPreAgreementDocument.name}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="document-upload">
                                    <input
                                        type="file"
                                        id="file-pre-agreement"
                                        className="d-none"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={handlePreAgreementFileChange}
                                        disabled={isUploading}
                                    />
                                    <label htmlFor="file-pre-agreement" className={`btn ${preAgreement.signedPreAgreementDocument ? 'btn-success' : 'btn-outline-primary'}`}>
                                        <i className={`fa ${preAgreement.signedPreAgreementDocument ? 'fa-check' : 'fa-upload'} me-2`}></i>
                                        {preAgreement.signedPreAgreementDocument ? 'Replace File' : 'Upload File'}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="document-section mb-4">
                    <div className="row g-4">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label className="form-label">Additional Notes</label>
                                <textarea
                                    className="form-control"
                                    name="preAgreementNotes"
                                    rows={4}
                                    value={preAgreement.preAgreementNotes}
                                    onChange={handleInputChange}
                                    placeholder="Enter any additional notes about the pre-agreement (e.g., signing date, parties involved, special conditions)"
                                    disabled={isUploading}
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between gap-2 mt-4">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleGoBack}
                        disabled={isUploading}
                    >
                        <i className="fa fa-arrow-left me-2"></i>
                        Back to Form
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleProceedToAgreementPreparation}
                        disabled={isUploading || !preAgreement.signedPreAgreementDocument}
                    >
                        <i className="fa fa-arrow-right me-2"></i>
                        Prepare Loan Agreement
                    </button>
                </div>
            </div>
        </div>
    );

    const renderUploadFinalAgreementStep = () => (
        <div className="card">
            <div className="card-header">
                <h6 className="card-title">
                    <i className="fa fa-file-upload text-primary me-2"></i>
                    Upload Signed Final Loan Agreement
                </h6>
            </div>
            <div className="card-body">
                {/* Generated Agreement PDF Download Section */}
                {generatedAgreementPdfBlob && (
                    <div className="alert alert-success mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <i className="fa fa-check-circle me-2"></i>
                                <strong>Loan agreement PDF generated successfully!</strong>
                                <p className="mb-0 mt-2">Download the generated loan agreement, get it signed, and upload it back.</p>
                            </div>
                            <button
                                className="btn btn-outline-success"
                                onClick={() => {
                                    if (!generatedAgreementPdfBlob) return;
                                    const url = window.URL.createObjectURL(generatedAgreementPdfBlob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = `loan-agreement-${loanApplication?.applicationNumber || 'document'}.pdf`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                    toast.success('Loan agreement PDF downloaded successfully');
                                }}
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-download me-2"></i>
                                        Download PDF
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                <p className="text-muted mb-4">
                    Upload the final signed loan agreement document. This is the legally binding agreement that will complete the loan process.
                </p>

                {!generatedAgreementPdfBlob && (
                    <div className="alert alert-info mb-4">
                        <div className="d-flex">
                            <div className="me-3">
                                <i className="fa fa-info-circle fa-lg"></i>
                            </div>
                            <div>
                                <p className="mb-0">
                                    <strong>Note:</strong> You skipped the loan agreement generation step. Please upload your existing signed loan agreement document.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="alert alert-info mb-4">
                    <div className="d-flex">
                        <div className="me-3">
                            <i className="fa fa-info-circle fa-lg"></i>
                        </div>
                        <div>
                            <p className="mb-0">
                                <strong>System requirements:</strong> PDF, JPG, PNG formats only. Maximum file size: 100MB per document.
                            </p>
                        </div>
                    </div>
                </div>

                {uploadError && (
                    <div className="alert alert-danger mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <span>{uploadError}</span>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setUploadError(null)}
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                )}

                <div className="document-section mb-4">
                    <div className="row g-4">
                        <div className="col-md-12">
                            <div className="document-upload-card">
                                <div className="document-info">
                                    <h6 className="document-title">
                                        Signed Final Loan Agreement <span className="text-danger">*</span>
                                    </h6>
                                    <p className="document-description">Upload the signed final loan agreement document</p>
                                    {agreement.signedDocument && (
                                        <div className="document-meta">
                                            <i className="fa fa-check-circle text-success me-2"></i>
                                            <span>{agreement.signedDocument.name}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="document-upload">
                                    <input
                                        type="file"
                                        id="file-final-agreement"
                                        className="d-none"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={handleFileChange}
                                        disabled={isUploading}
                                    />
                                    <label htmlFor="file-final-agreement" className={`btn ${agreement.signedDocument ? 'btn-success' : 'btn-outline-primary'}`}>
                                        <i className={`fa ${agreement.signedDocument ? 'fa-check' : 'fa-upload'} me-2`}></i>
                                        {agreement.signedDocument ? 'Replace File' : 'Upload File'}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="document-section mb-4">
                    <div className="row g-4">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label className="form-label">Additional Notes</label>
                                <textarea
                                    className="form-control"
                                    name="notes"
                                    rows={4}
                                    value={agreement.notes}
                                    onChange={handleInputChange}
                                    placeholder="Enter any additional notes about the final agreement (e.g., special clauses, parties involved, date signed)"
                                    disabled={isUploading}
                                ></textarea>
                                <div className="form-text">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Add any important details about the agreement, signatories, or special conditions
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="document-section">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h6 className="section-subtitle mb-0">Legal Verification Checklist</h6>
                    </div>

                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="d-flex align-items-center p-3 bg-light rounded-3 mb-2">
                                <i className="bi bi-check-circle-fill me-2 text-success"></i>
                                <span>The agreement has been properly signed by all parties</span>
                            </div>
                            <div className="d-flex align-items-center p-3 bg-light rounded-3 mb-2">
                                <i className="bi bi-check-circle-fill me-2 text-success"></i>
                                <span>This is the final, legally binding version</span>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center p-3 bg-light rounded-3 mb-2">
                                <i className="bi bi-check-circle-fill me-2 text-success"></i>
                                <span>All parties have received copies of the agreement</span>
                            </div>
                            <div className="d-flex align-items-center p-3 bg-light rounded-3 mb-2">
                                <i className="bi bi-check-circle-fill me-2 text-success"></i>
                                <span>The document contains all required terms and conditions</span>
                            </div>
                        </div>
                    </div>

                    <div className="form-check mt-3 p-3 border rounded-3">
                        <input
                            className="form-check-input ms-0 me-2"
                            type="checkbox"
                            id="confirmSignedAgreement"
                            checked={confirmedSigned}
                            onChange={(e) => setConfirmedSigned(e.target.checked)}
                            disabled={isUploading || !agreement.signedDocument}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="confirmSignedAgreement">
                            I confirm this is the loan agreement signed by the client
                        </label>
                        <div className="form-text">
                            Submitting records the agreement as signed by you, today. Uploading a newer copy later
                            replaces it and keeps this one as a previous version.
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between gap-2 mt-4">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleGoBack}
                        disabled={isUploading}
                    >
                        <i className="fa fa-arrow-left me-2"></i>
                        Back to Agreement Preparation
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={isUploading || !agreement.signedDocument || !confirmedSigned}
                    >
                        {isUploading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-cloud-arrow-up me-2"></i>
                                Submit Final Agreement
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="loan-agreement-details">
            {/* Add loading overlay */}
            {showLoadingOverlay && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <div className="loading-text">Processing your request</div>
                        <div className="loading-subtext">Please wait while we handle your document</div>
                    </div>
                </div>
            )}

            <div className="page-toolbar">
                <div className="container-fluid">
                    <div className="row g-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                <li className="breadcrumb-item"><Link to="/work-items">Work Items</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Loan Agreement</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-fluid">
                    {/* Modern Loan Details Card */}
                    <div className="loan-details-card">
                        <div className="loan-details-header">
                            <div className="loan-id">
                                <span className="label">Application</span>
                                <h2>{loanApplication?.applicationNumber}</h2>
                            </div>
                            <div className="loan-status">
                                <span className="badge bg-warning">{loanApplication?.status}</span>
                                <button
                                    className="btn btn-sm btn-outline-danger ms-2"
                                    onClick={() => setShowCancelModal(true)}
                                >
                                    <i className="fa fa-times-circle me-1"></i> Cancel Application
                                </button>
                            </div>
                        </div>
                        <div className="loan-details-body">
                            <div className="loan-details-grid">
                                <div className="loan-detail-item">
                                    <span className="detail-label">Customer</span>
                                    <span className="detail-value">
                                        {loanApplication?.loanType === 'BUSINESS'
                                            ? loanApplication.company?.companyName || 'N/A'
                                            : loanApplication?.individual?.fullNames || 'N/A'}
                                    </span>
                                </div>
                                <div className="loan-detail-item highlight">
                                    <span className="detail-label">Loan Amount</span>
                                    <span className="detail-value">
                                        R {loanApplication?.loanDetails?.loanAmount?.toLocaleString() || 'N/A'}
                                    </span>
                                </div>
                                <div className="loan-detail-item">
                                    <span className="detail-label">Term</span>
                                    <span className="detail-value">
                                        {loanApplication?.loanDetails?.numberOfInstallments === 1
                                            ? `${loanApplication?.loanDetails?.loanTerms} days`
                                            : `${loanApplication?.loanDetails?.numberOfInstallments} months`}
                                    </span>
                                </div>
                                <div className="loan-detail-item">
                                    <span className="detail-label">Application Date</span>
                                    <span className="detail-value">
                                        {loanApplication?.createdDate
                                            ? new Date(loanApplication.createdDate).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            })
                                            : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Workflow Progress Indicator */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="workflow-steps">
                                <div className={`step ${currentStep === 'prepare' ? 'active' : 'completed'}`}>
                                    <div className="step-icon">
                                        <i className={`fa ${currentStep === 'prepare' ? 'fa-edit' : 'fa-check'}`}></i>
                                    </div>
                                    <div className="step-content">
                                        <h6>Prepare Pre-Agreement</h6>
                                        <p>Review and edit pre-agreement data</p>
                                    </div>
                                </div>
                                <div className="step-connector"></div>
                                <div className={`step ${currentStep === 'upload-pre-agreement' ? 'active' : ['prepare-agreement', 'upload-final-agreement', 'completed'].includes(currentStep) ? 'completed' : ''}`}>
                                    <div className="step-icon">
                                        <i className={`fa ${currentStep === 'upload-pre-agreement' ? 'fa-upload' : ['prepare-agreement', 'upload-final-agreement', 'completed'].includes(currentStep) ? 'fa-check' : 'fa-upload'}`}></i>
                                    </div>
                                    <div className="step-content">
                                        <h6>Upload Signed Pre-Agreement</h6>
                                        <p>Upload the signed pre-agreement document</p>
                                    </div>
                                </div>
                                <div className="step-connector"></div>
                                <div className={`step ${currentStep === 'prepare-agreement' ? 'active' : ['upload-final-agreement', 'completed'].includes(currentStep) ? 'completed' : ''}`}>
                                    <div className="step-icon">
                                        <i className={`fa ${currentStep === 'prepare-agreement' ? 'fa-edit' : ['upload-final-agreement', 'completed'].includes(currentStep) ? 'fa-check' : 'fa-edit'}`}></i>
                                    </div>
                                    <div className="step-content">
                                        <h6>Prepare Final Agreement</h6>
                                        <p>Review and edit final agreement data</p>
                                    </div>
                                </div>
                                <div className="step-connector"></div>
                                <div className={`step ${currentStep === 'upload-final-agreement' ? 'active' : currentStep === 'completed' ? 'completed' : ''}`}>
                                    <div className="step-icon">
                                        <i className={`fa ${currentStep === 'upload-final-agreement' ? 'fa-file-contract' : currentStep === 'completed' ? 'fa-check' : 'fa-file-contract'}`}></i>
                                    </div>
                                    <div className="step-content">
                                        <h6>Upload Final Agreement</h6>
                                        <p>Upload the signed final loan agreement</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step Content */}
                    {renderStepContent()}
                </div>
            </div>

            {/* Cancel Application Modal */}
            {showCancelModal && (
                <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                            <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.3)' }}>
                                        <i className="fa fa-times-circle text-danger" style={{ display: 'block' }}></i>
                                    </div>
                                    <div>
                                        <h5 className="modal-title mb-0 fw-bold">Cancel Application</h5>
                                        <p className="text-muted mb-0 small">This action cannot be undone</p>
                                    </div>
                                </div>
                                <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)} disabled={isCancelling}></button>
                            </div>
                            <div className="modal-body p-4">
                                <p className="text-muted mb-3">Are you sure you want to cancel this loan application? Please provide a reason:</p>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    placeholder="Enter reason for cancellation..."
                                    disabled={isCancelling}
                                    maxLength={500}
                                />
                                <small className="text-muted">{cancelReason.length}/500 characters</small>
                            </div>
                            <div className="modal-footer border-0 px-4 pb-4">
                                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger rounded-pill px-4"
                                    onClick={handleCancelApplication}
                                    disabled={isCancelling || !cancelReason.trim()}
                                >
                                    {isCancelling ? (
                                        <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Cancelling...</>
                                    ) : (
                                        <><i className="fa fa-times-circle me-2"></i>Cancel Application</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoanAgreementDetails;
